/**
 * AuthProvider — single source of truth for "who is logged in".
 *
 * Stage 3.2 — full auth flow:
 *   1. tokenAuth(email, password) → JWT (gqlauth)
 *   2. me → user (our own MeType, includes role + first/last name)
 *
 * Step 2 succeeds because the backend JWT-auth middleware
 * (kurdishrojava.jwt_middleware.JwtAuthenticationMiddleware) populates
 * request.user from the Authorization header.
 *
 * Both steps use plain fetch — using urql here would couple bootstrap
 * to the already-created client + create a circular dep with main.tsx.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import {
  clearAuth,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
  type StoredUser,
} from "./token";

export type Role = "coordinator" | "secretary" | "treasurer" | "editor" | "member";

interface AuthContextValue {
  user: StoredUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_AUTH_MUTATION = /* GraphQL */ `
  mutation TokenAuth($email: String!, $password: String!) {
    tokenAuth(email: $email, password: $password) {
      success
      errors
      token {
        token
      }
    }
  }
`;

const ME_QUERY = /* GraphQL */ `
  query Me {
    me {
      email
      role
      firstName
      lastName
    }
  }
`;

interface TokenAuthResponse {
  data?: {
    tokenAuth?: {
      success: boolean;
      errors?: unknown;
      token?: { token: string };
    };
  };
  errors?: Array<{ message: string }>;
}

interface MeResponse {
  data?: {
    me?: {
      email: string;
      role: string;
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  };
  errors?: Array<{ message: string }>;
}

function graphqlEndpoint(): string {
  // In dev, fall back to /graphql/ so Vite's proxy can hand off to the API
  // (avoids CORS during local development). In production, the admin is
  // served from admin.kurdishrojava.org.uk and must talk to api.* directly
  // — nginx on the admin vhost intentionally has no /graphql/ location.
  if (import.meta.env.VITE_GRAPHQL_URL) return import.meta.env.VITE_GRAPHQL_URL;
  return import.meta.env.PROD
    ? "https://api.kurdishrojava.org.uk/graphql/"
    : "/graphql/";
}

function formatGqlAuthErrors(errors: unknown): string {
  if (typeof errors === "string") return errors;
  if (errors && typeof errors === "object") {
    try {
      const collected: string[] = [];
      for (const [field, value] of Object.entries(errors)) {
        if (Array.isArray(value)) {
          for (const v of value) {
            if (typeof v === "string") collected.push(v);
            else if (v && typeof v === "object" && "message" in v) {
              collected.push(String((v as { message: unknown }).message));
            }
          }
        } else if (typeof value === "string") {
          collected.push(`${field}: ${value}`);
        }
      }
      if (collected.length) return collected.join("\n");
    } catch {
      /* fall through */
    }
  }
  return "Login failed — check your email and password.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<StoredUser | null>(() => getStoredUser());
  const [token, setTokenState] = useState<string | null>(() => getToken());

  const login = useCallback(async (email: string, password: string) => {
    // Step 1 — obtain JWT.
    const tokenRes = await fetch(graphqlEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: TOKEN_AUTH_MUTATION,
        variables: { email, password },
      }),
    });
    if (!tokenRes.ok) {
      throw new Error(`Login request failed: ${tokenRes.status} ${tokenRes.statusText}`);
    }
    const tokenBody = (await tokenRes.json()) as TokenAuthResponse;
    if (tokenBody.errors?.length) {
      throw new Error(tokenBody.errors.map((e) => e.message).join("\n"));
    }
    const tokenPayload = tokenBody.data?.tokenAuth;
    if (!tokenPayload?.success || !tokenPayload.token?.token) {
      throw new Error(formatGqlAuthErrors(tokenPayload?.errors));
    }
    const jwt = tokenPayload.token.token;

    // Step 2 — fetch our own MeType (role + names) using the new JWT.
    const meRes = await fetch(graphqlEndpoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${jwt}`,
      },
      body: JSON.stringify({ query: ME_QUERY }),
    });
    if (!meRes.ok) {
      throw new Error(`Profile request failed: ${meRes.status} ${meRes.statusText}`);
    }
    const meBody = (await meRes.json()) as MeResponse;
    if (meBody.errors?.length) {
      throw new Error(`Logged in but couldn't load profile: ${meBody.errors[0].message}`);
    }
    const me = meBody.data?.me;
    if (!me) {
      throw new Error(
        "Logged in but the server didn't return your profile. " +
          "The JWT auth middleware may not be deployed yet.",
      );
    }

    const stored: StoredUser = {
      email: me.email,
      role: me.role,
      firstName: me.firstName,
      lastName: me.lastName,
    };

    setToken(jwt);
    setStoredUser(stored);
    setTokenState(jwt);
    setUser(stored);
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setTokenState(null);
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  const hasRole = useCallback(
    (...roles: Role[]) => Boolean(user && (roles as string[]).includes(user.role)),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      login,
      logout,
      hasRole,
    }),
    [user, token, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
